import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../context/AuthContext';
import '../styles/general.css';

/**
 * Sidebar navigation.
 *
 * The menu is grouped by kind of movement rather than listed flat: nine
 * top-level entries had become hard to scan. Groups behave as an accordion —
 * opening one closes the others — because three groups expanded at once puts
 * the sidebar back where it started.
 *
 * That accordion is why the open group is a single value rather than one
 * boolean per group: with booleans, "only one open" is a rule you have to
 * remember to enforce on every toggle. Here it falls out of the data.
 */
const Navigation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, canAccess } = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);

  const handleNavigate = (path) => {
    navigate(path);
    setIsOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsOpen(false);
  };

  const toggleGroup = (group) => {
    setOpenGroup((current) => (current === group ? null : group));
  };

  const navItem = (labelKey, path) => (
    <div
      key={path}
      onClick={() => handleNavigate(path)}
      className="nav-item"
    >
      {t(labelKey)}
    </div>
  );

  // Children are declared with the module they belong to, so the menu can be
  // filtered from the same permission list the routes and the API use.
  const allowed = (children) =>
    children.filter(([, , module]) => !module || (canAccess && canAccess(module)));

  const navGroup = (group, labelKey, children) => {
    const visible = allowed(children);

    // A group whose children are all invisible is noise, not an empty folder:
    // it advertises an area the user cannot reach.
    if (visible.length === 0) return null;

    return (
      <>
        <div onClick={() => toggleGroup(group)} className="nav-parent">
          <span>{t(labelKey)}</span>
          <span className={`nav-parent-caret ${openGroup === group ? 'open' : ''}`}>
            ▶
          </span>
        </div>

        {openGroup === group && (
          <div className="nav-children">
            {visible.map(([childKey, path]) => navItem(childKey, path))}
          </div>
        )}
      </>
    );
  };

  return (
    <>
      {/* Hamburger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="nav-hamburger"
      >
        ☰
      </button>

      {/* Overlay */}
      {isOpen && (
        <div
          onClick={() => setIsOpen(false)}
          className="nav-overlay"
        />
      )}

      {/* Sidebar */}
      <nav className={`nav-sidebar ${isOpen ? 'open' : 'closed'}`}>
        <div className="nav-content">
          <h2 className="nav-brand">Brintter</h2>

          {user && (
            <>
              {/* Day-to-day operations first, administration below. */}
              {navGroup('sales', 'navigation.salesMenu', [
                ['navigation.clients', '/clients', 'clients'],
                ['navigation.clientQuotes', '/client-quotes', 'quotes'],
                // The one entry that is an action rather than a destination:
                // creating a quote is frequent enough to earn a shortcut.
                ['navigation.createQuote', '/client-quotes/create', 'quotes'],
                ['navigation.sales', '/sales', 'sales'],
              ])}

              {navGroup('purchases', 'navigation.purchasesMenu', [
                ['navigation.suppliers', '/suppliers', 'suppliers'],
                ['navigation.purchases', '/purchases', 'purchases'],
              ])}
            </>
          )}

          {user && canAccess && canAccess('products')
            && navItem('navigation.products', '/products')}

          {/* Company administration rather than daily operation, so it sits
              below the operational groups and above payroll. */}
          {user && canAccess && canAccess('documents')
            && navItem('navigation.files', '/files')}

          {user && (
            <>
              {/* Employees lives in here, not as a loose item: the record
                  carries salary and banking data, and only roles holding the
                  employees module can read it. */}
              {navGroup('payroll', 'navigation.payrollMenu', [
                ['navigation.employees', '/employees', 'employees'],
                ['navigation.payroll', '/payroll', 'payroll'],
              ])}
            </>
          )}

          {user && (
            <>
              {/* A single-child group on purpose, unlike the loose Archivos
                  entry: MonetaryMovement and MonetaryClosing are already
                  specified to hang here, so this one grows. */}
              {navGroup('accounting', 'navigation.accountingMenu', [
                ['navigation.monetaryAccounts', '/monetary-accounts', 'accounting'],
              ])}
            </>
          )}

          {/* User administration is not a module: the API still gates it on
              is_staff, so the menu does too. */}
          {user?.is_staff && navItem('navigation.users', '/users')}

          <div
            onClick={handleLogout}
            className="nav-logout"
          >
            {t('navigation.logout')}
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navigation;
